import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../config/firebase";

const DEMO_REQUEST_STATUSES = [
  "pending",
  "reviewed",
  "approved",
  "rejected",
  "converted",
];

const PAGE_SIZE = 10;

const statusBadgeClass = (status) => {
  switch (status) {
    case "approved":
    case "converted":
      return "success";
    case "rejected":
      return "danger";
    case "reviewed":
      return "info";
    case "pending":
    default:
      return "warning";
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString("tr-TR");
  }
  if (typeof value === "number") {
    return new Date(value).toLocaleString("tr-TR");
  }
  if (typeof value === "string") return value;
  return "-";
};

const textOrDash = (value) => {
  if (value === null || value === undefined) return "-";
  const next = String(value).trim();
  return next ? next : "-";
};

function DemoRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateError, setUpdateError] = useState("");

  const fetchRequests = async () => {
    setIsLoading(true);
    setError("");
    try {
      const requestsRef = collection(db, "demo_requests");
      const requestsQuery = query(requestsRef, orderBy("created_at", "desc"));
      const snapshot = await getDocs(requestsQuery);
      const list = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      setRequests(list);
    } catch (err) {
      setError(err?.message || "Demo talepleri yüklenemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return requests;

    return requests.filter((request) => {
      const company = (request?.company_name || "").toLowerCase();
      const contact = (request?.contact_full_name || "").toLowerCase();
      const email = (request?.email || "").toLowerCase();
      const phone = (request?.phone || "").toLowerCase();
      return (
        company.includes(term) ||
        contact.includes(term) ||
        email.includes(term) ||
        phone.includes(term)
      );
    });
  }, [requests, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedRequests = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRequests.slice(start, start + PAGE_SIZE);
  }, [filteredRequests, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setSelectedStatus(request?.status || "pending");
    setUpdateError("");
    setUpdateMessage("");
  };

  const closeRequestDetail = () => {
    setSelectedRequest(null);
    setUpdateError("");
    setUpdateMessage("");
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequest?.id) return;

    setIsUpdatingStatus(true);
    setUpdateError("");
    setUpdateMessage("");

    try {
      const requestRef = doc(db, "demo_requests", selectedRequest.id);
      await updateDoc(requestRef, {
        status: selectedStatus,
        updated_at: serverTimestamp(),
      });

      setRequests((prev) =>
        prev.map((item) =>
          item.id === selectedRequest.id
            ? { ...item, status: selectedStatus, updated_at: new Date() }
            : item
        )
      );
      setSelectedRequest((prev) =>
        prev ? { ...prev, status: selectedStatus, updated_at: new Date() } : prev
      );
      setUpdateMessage("Durum başarıyla güncellendi.");
    } catch (err) {
      setUpdateError(err?.message || "Durum güncellenemedi.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan="6">Yükleniyor...</td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td colSpan="6" className="text-danger">
            {error}
          </td>
        </tr>
      );
    }

    if (pagedRequests.length === 0) {
      return (
        <tr>
          <td colSpan="6">Kayıt bulunamadı.</td>
        </tr>
      );
    }

    return pagedRequests.map((request) => (
      <tr
        key={request.id}
        style={{ cursor: "pointer" }}
        onClick={() => openRequestDetail(request)}
      >
        <td>{textOrDash(request?.company_name)}</td>
        <td>{textOrDash(request?.contact_full_name)}</td>
        <td>{textOrDash(request?.email)}</td>
        <td>{textOrDash(request?.phone)}</td>
        <td>
          <span className={`badge badge-${statusBadgeClass(request?.status)}`}>
            {textOrDash(request?.status)}
          </span>
        </td>
        <td>{formatDate(request?.created_at)}</td>
      </tr>
    ));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">Demo Talepleri</h4>

            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Firma, yetkili, e-posta veya telefon ara"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th>Yetkili</th>
                    <th>E-posta</th>
                    <th>Telefon</th>
                    <th>Durum</th>
                    <th>Oluşturma</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">Toplam kayıt: {filteredRequests.length}</span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Önceki
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedRequest ? (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">Demo Talep Detayı</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeRequestDetail}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body" style={{ overflowY: "auto" }}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Talep ID:</strong> {textOrDash(selectedRequest?.id)}
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Durum:</strong>{" "}
                    <span className={`badge badge-${statusBadgeClass(selectedRequest?.status)}`}>
                      {textOrDash(selectedRequest?.status)}
                    </span>
                  </div>

                  <div className="col-md-6 mb-3"><strong>Firma Adı:</strong> {textOrDash(selectedRequest?.company_name)}</div>
                  <div className="col-md-6 mb-3"><strong>Yetkili:</strong> {textOrDash(selectedRequest?.contact_full_name)}</div>
                  <div className="col-md-6 mb-3"><strong>E-posta:</strong> {textOrDash(selectedRequest?.email)}</div>
                  <div className="col-md-6 mb-3"><strong>Telefon:</strong> {textOrDash(selectedRequest?.phone)}</div>
                  <div className="col-md-6 mb-3"><strong>Web Sitesi:</strong> {textOrDash(selectedRequest?.website)}</div>
                  <div className="col-md-6 mb-3"><strong>Sektör:</strong> {textOrDash(selectedRequest?.sector)}</div>
                  <div className="col-md-6 mb-3"><strong>Çalışan Sayısı:</strong> {textOrDash(selectedRequest?.employee_count)}</div>
                  <div className="col-md-6 mb-3"><strong>Aylık Toplantı:</strong> {textOrDash(selectedRequest?.estimated_monthly_meetings)}</div>
                  <div className="col-md-6 mb-3"><strong>KVKK Onayı:</strong> {selectedRequest?.kvkk_consent ? "Evet" : "Hayır"}</div>
                  <div className="col-md-6 mb-3"><strong>Ticari İleti:</strong> {selectedRequest?.marketing_consent ? "Evet" : "Hayır"}</div>
                  <div className="col-md-6 mb-3"><strong>Paket:</strong> {textOrDash(selectedRequest?.demo_package)}</div>
                  <div className="col-md-6 mb-3"><strong>Kaynak:</strong> {textOrDash(selectedRequest?.source)}</div>
                  <div className="col-md-6 mb-3"><strong>Oluşturma:</strong> {formatDate(selectedRequest?.created_at)}</div>
                  <div className="col-md-6 mb-3"><strong>Güncelleme:</strong> {formatDate(selectedRequest?.updated_at)}</div>

                  <div className="col-12 mb-3">
                    <strong>Demo Kullanım Amacı:</strong>
                    <div className="mt-1 p-2 border rounded bg-light">
                      {textOrDash(selectedRequest?.demo_purpose)}
                    </div>
                  </div>
                </div>

                <hr />

                <div className="row align-items-end">
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><strong>Durum Güncelle</strong></label>
                    <select
                      className="form-control"
                      value={selectedStatus}
                      onChange={(event) => setSelectedStatus(event.target.value)}
                    >
                      {DEMO_REQUEST_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleStatusUpdate}
                      disabled={isUpdatingStatus}
                    >
                      {isUpdatingStatus ? "Güncelleniyor..." : "Durumu Kaydet"}
                    </button>
                  </div>
                </div>

                {updateMessage ? <div className="alert alert-success py-2">{updateMessage}</div> : null}
                {updateError ? <div className="alert alert-danger py-2">{updateError}</div> : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeRequestDetail}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DemoRequestsPage;
